import { MysqlKind, ResourceName, ServerRole, Subscription, WebserverKind } from 'src/orchd-client';
import { Box } from '../Box';
import { Text } from '../Text';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { SquarePill } from '../SquarePill/SquarePill';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { IconName } from '../Icon/Icon.types';
import { Colors } from 'src/design-system/theme';
import { Responsive } from 'src/design-system/style-types';
import { subscriptionHasResource } from 'src/utils/packages/utils';
import { serversApi } from 'src/api_services/servers/service';
import { useEffect, useState } from 'react';
import { mysqlKindIcons, webserverIcons } from 'src/pages/packages/SubscriptionServers';

interface Props {
  subscription: Subscription;
  isMasterOrg: boolean;
  isDisabled: boolean;
}

const DediRolePill = () => {
  const { formatMessage } = useIntl();

  return (
    <SquarePill
      size="sm"
      text={formatMessage({ id: 'dedicated_package_pill.text', defaultMessage: 'dedi' })}
      tooltipText={formatMessage({
        id: 'dedicated_package_pill.tooltip_end_user',
        defaultMessage:
          'This role is dedicated to a specific server.  Server resources are dedicated to your websites and not shared with any other customer.',
      })}
      showTooltip
      color="azure"
      disabled={false}
      mr="md"
    />
  );
};

interface DisplayRoleProps {
  roleKind: ServerRole;
  serverId?: string;
  icon: IconName;
  isMasterOrg: boolean;
  background?: Responsive<Colors>;
  isDisabled: boolean;
}

export const DisplayRole = ({ roleKind, serverId, icon, isMasterOrg, background, isDisabled }: DisplayRoleProps) => {
  const history = useHistory();
  return (
    <Box j="space-between" mb="sm" bg={background} p={background ? 'sm' : undefined} a="center">
      <Box w={background ? 8 : 12} a="center">
        <Icon name={icon} marginRight="sm" />
        <Text size="xs" transform="uppercase" marginRight="sm" color={isDisabled ? 'grey' : undefined}>
          {roleKind}
        </Text>
        {serverId && <DediRolePill />}
      </Box>

      <Box w={4} j="flex-end">
        {serverId && (
          <Button
            variant="tertiary"
            size="small"
            onClick={() => history.push(isMasterOrg ? `/servers/manage/${serverId}` : '/subscription-servers')}
            color={isDisabled ? 'grey' : undefined}
            disabled={isDisabled}
          >
            Manage server
          </Button>
        )}
      </Box>
    </Box>
  );
};

export const DisplayPackageServers = ({ subscription, isMasterOrg, isDisabled }: Props) => {
  const hasEmails =
    subscriptionHasResource(subscription, ResourceName.mailboxes) ||
    subscriptionHasResource(subscription, ResourceName.forwarders);

  const hasBackups = subscription.allowances.find((allowance) => allowance.name === 'backupsAllowAutomatic');

  const [webServerKind, setWebServerKind] = useState<WebserverKind>();
  const [mysqlKind, setMysqlKind] = useState<MysqlKind>();

  useEffect(() => {
    if (subscription?.dedicatedServers?.appServer) {
      serversApi
        .getServerRoles({
          serverId: subscription.dedicatedServers?.appServer.id,
        })
        .then((roles) => {
          roles.data.webserverKind && setWebServerKind(roles.data.webserverKind);
        });
    }

    if (subscription?.dedicatedServers?.dbServer) {
      serversApi
        .getServerRoles({
          serverId: subscription.dedicatedServers?.dbServer.id,
        })
        .then((roles) => {
          roles.data.mysqlKind && setMysqlKind(roles.data.mysqlKind);
        });
    }
  }, [subscription]);

  return (
    <>
      <DisplayRole
        roleKind={ServerRole.application}
        serverId={subscription.dedicatedServers?.appServer?.id}
        icon={webServerKind ? webserverIcons[webServerKind] : 'application_window'}
        isMasterOrg={isMasterOrg}
        background={isDisabled ? 'grey3' : 'grey5'}
        isDisabled={isDisabled}
      />
      <DisplayRole
        roleKind={ServerRole.database}
        serverId={subscription.dedicatedServers?.dbServer?.id}
        icon={mysqlKind ? mysqlKindIcons[mysqlKind] : 'database_stack'}
        isMasterOrg={isMasterOrg}
        background={isDisabled ? 'grey3' : 'grey5'}
        isDisabled={isDisabled}
      />
      <DisplayRole
        roleKind={ServerRole.postgresql}
        serverId={subscription.dedicatedServers?.postgresqlServer?.id}
        icon="postgresql"
        isMasterOrg={isMasterOrg}
        background={isDisabled ? 'grey3' : 'grey5'}
        isDisabled={isDisabled}
      />
      {hasEmails && (
        <DisplayRole
          roleKind={ServerRole.email}
          serverId={subscription.dedicatedServers?.emailServer?.id}
          icon="mail_envelope"
          isMasterOrg={isMasterOrg}
          background={isDisabled ? 'grey3' : 'grey5'}
          isDisabled={isDisabled}
        />
      )}
      {hasBackups && (
        <DisplayRole
          roleKind={ServerRole.backup}
          serverId={subscription.dedicatedServers?.backupServer?.id}
          icon="cloud_upload"
          isMasterOrg={isMasterOrg}
          background={isDisabled ? 'grey3' : 'grey5'}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
};
